import { defineComponent, h } from 'vue';
import { aliases as mdiSvgAliases } from 'vuetify/iconsets/mdi-svg';
import type { IconSet } from 'vuetify';
import {
  mdiAccount,
  mdiAccountCircle,
  mdiAccountEdit,
  mdiAccountMultiple,
  mdiAccountSearch,
  mdiAccountSupervisor,
  mdiAlert,
  mdiAlertCircle,
  mdiAppleKeyboardControl,
  mdiArrowLeft,
  mdiBell,
  mdiCalendarAccountOutline,
  mdiCalendarBlank,
  mdiCalendarCheck,
  mdiCalendarSearch,
  mdiCash,
  mdiChartBar,
  mdiCheck,
  mdiCheckCircle,
  mdiCheckboxMarkedCircle,
  mdiCheckboxMarkedCircleOutline,
  mdiClose,
  mdiCloseCircle,
  mdiCog,
  mdiContentCopy,
  mdiDelete,
  mdiDotsHorizontal,
  mdiDotsVertical,
  mdiEye,
  mdiEyeOff,
  mdiFlag,
  mdiInformation,
  mdiKeyboard,
  mdiLogout,
  mdiMagnify,
  mdiMapMarker,
  mdiMessageText,
  mdiMotionPlayOutline,
  mdiPause,
  mdiPlay,
  mdiPlus,
  mdiRefresh,
  mdiShieldCheck,
  mdiTriangle,
  mdiVideo,
  mdiViewDashboard,
} from '@mdi/js';

// Map kebab-case icon names (used in templates) to @mdi/js SVG path data
const iconPaths: Record<string, string> = {
  'mdi-account': mdiAccount,
  'mdi-account-circle': mdiAccountCircle,
  'mdi-account-edit': mdiAccountEdit,
  'mdi-account-multiple': mdiAccountMultiple,
  'mdi-account-search': mdiAccountSearch,
  'mdi-account-supervisor': mdiAccountSupervisor,
  'mdi-alert': mdiAlert,
  'mdi-alert-circle': mdiAlertCircle,
  'mdi-apple-keyboard-control': mdiAppleKeyboardControl,
  'mdi-arrow-left': mdiArrowLeft,
  'mdi-bell': mdiBell,
  'mdi-calendar-account-outline': mdiCalendarAccountOutline,
  'mdi-calendar-blank': mdiCalendarBlank,
  'mdi-calendar-check': mdiCalendarCheck,
  'mdi-calendar-search': mdiCalendarSearch,
  'mdi-cash': mdiCash,
  'mdi-chart-bar': mdiChartBar,
  'mdi-check': mdiCheck,
  'mdi-check-circle': mdiCheckCircle,
  'mdi-checkbox-marked-circle': mdiCheckboxMarkedCircle,
  'mdi-checkbox-marked-circle-outline': mdiCheckboxMarkedCircleOutline,
  'mdi-close': mdiClose,
  'mdi-close-circle': mdiCloseCircle,
  'mdi-cog': mdiCog,
  'mdi-content-copy': mdiContentCopy,
  'mdi-delete': mdiDelete,
  'mdi-dots-horizontal': mdiDotsHorizontal,
  'mdi-dots-vertical': mdiDotsVertical,
  'mdi-eye': mdiEye,
  'mdi-eye-off': mdiEyeOff,
  'mdi-flag': mdiFlag,
  'mdi-information': mdiInformation,
  'mdi-keyboard': mdiKeyboard,
  'mdi-logout': mdiLogout,
  'mdi-magnify': mdiMagnify,
  'mdi-map-marker': mdiMapMarker,
  'mdi-message-text': mdiMessageText,
  'mdi-motion-play-outline': mdiMotionPlayOutline,
  'mdi-pause': mdiPause,
  'mdi-play': mdiPlay,
  'mdi-plus': mdiPlus,
  'mdi-refresh': mdiRefresh,
  'mdi-shield-check': mdiShieldCheck,
  'mdi-triangle': mdiTriangle,
  'mdi-video': mdiVideo,
  'mdi-view-dashboard': mdiViewDashboard,
};

// Custom icon set component — resolves mdi-* names to SVG paths from @mdi/js
// Handles both: direct names ("mdi-close") and Vuetify alias values ("svg:M19,6...")
const CustomMdiSvgIcon = defineComponent({
  name: 'CustomMdiSvgIcon',
  props: {
    icon: { type: [String, Function], required: true },
    tag: { type: String, required: true },
  },
  setup(props) {
    return () => {
      let pathData = '';
      const iconName = typeof props.icon === 'string' ? props.icon : '';
      const style: Record<string, string> = {};

      if (iconName.startsWith('svg:')) {
        // Vuetify internal aliases pass "svg:M19,6.41..." format
        pathData = iconName.slice(4);
      } else {
        // Template icons pass "mdi-close" or "mdi-close mdi-rotate-180" format
        const parts = iconName.split(' ');
        pathData = iconPaths[parts[0]] || '';

        // Handle CSS modifiers (e.g., mdi-rotate-180, mdi-18px)
        for (let i = 1; i < parts.length; i++) {
          if (parts[i] === 'mdi-rotate-180') style.transform = 'rotate(180deg)';
          else if (parts[i] === 'mdi-rotate-90') style.transform = 'rotate(90deg)';
          else if (parts[i] === 'mdi-rotate-270') style.transform = 'rotate(270deg)';
          else if (parts[i] === 'mdi-18px') { style.width = '18px'; style.height = '18px'; }
          else if (parts[i] === 'mdi-24px') { style.width = '24px'; style.height = '24px'; }
        }
      }

      return h(props.tag, [
        h('svg', {
          class: 'v-icon__svg',
          xmlns: 'http://www.w3.org/2000/svg',
          viewBox: '0 0 24 24',
          role: 'img',
          'aria-hidden': true,
          ...(Object.keys(style).length > 0 ? { style } : {}),
        }, [
          h('path', { d: pathData }),
        ]),
      ]);
    };
  },
});

export const customMdiSvg: IconSet = {
  component: CustomMdiSvgIcon as any,
};

// Vuetify's built-in aliases (for $close, $dropdown, $checkboxOn, etc.)
export const aliases = mdiSvgAliases;
